import { useMemo, useState } from 'react';
import type { HourException, Staff, TimeOff, TimeOffReason } from '@salonomy/types';
import { Button, Card, Field, Pill, Select, TextArea, TextInput } from '@salonomy/ui';
import {
  useCreateTimeOff,
  useDeleteHourException,
  useDeleteTimeOff,
  useHourExceptions,
  useSetHourException,
  useTimeOff,
} from '../../data/scheduling.js';
import { longDate, spanLabel, todayInZone } from '../../lib/time.js';
import { useToast } from '../../lib/toast.js';
import { WorkingHoursGrid } from './WorkingHoursGrid.js';

/**
 * WS7 phase-1 rota editor — the real surface that replaces brief-06's "Rota & working hours"
 * Coming-soon. A per-staff panel to list/add/remove time off and per-date working-hour exceptions
 * (closed / custom hours / break). Adding time off that clashes with existing bookings surfaces the
 * conflicts (rebook/notify) — never hides them. RBAC mirrors the API: a manager picks any staff
 * member; a chair_renter manages only their own (the caller passes the staff list it's allowed to
 * show and whether the picker is locked).
 */

const REASON_LABEL: Record<TimeOffReason, string> = {
  holiday: 'Holiday',
  sick: 'Sick',
  training: 'Training',
  other: 'Time off',
};
const REASONS = Object.entries(REASON_LABEL) as Array<[TimeOffReason, string]>;

export function RotaEditor({
  staff,
  tz,
  lockedStaffId,
  selfStaffId,
}: {
  /** The staff a manager may edit, or just `[me]` for a chair_renter. */
  staff: Staff[];
  tz: string;
  /** When set (chair_renter), the picker is fixed to this member. */
  lockedStaffId?: string | null;
  /**
   * The actor's own staff record id. When set, that member is always offered in the picker — so a
   * MANAGER (whose role isn't one of the bookable-chair roles) can still set their OWN time off and
   * base hours from this UI, not only other people's. Mirrors the API's manager-or-self rule.
   */
  selfStaffId?: string | null;
}) {
  const chairs = useMemo(
    () =>
      staff.filter(
        (s) => ['owner', 'stylist', 'chair_renter'].includes(s.role) || s.id === selfStaffId,
      ),
    [staff, selfStaffId],
  );
  const [selectedId, setSelectedId] = useState<string>(lockedStaffId ?? chairs[0]?.id ?? '');
  const selected = chairs.find((s) => s.id === selectedId) ?? chairs[0];

  if (!selected) {
    return (
      <Card>
        <div className="section-title">Rota &amp; working hours</div>
        <div className="muted" style={{ fontSize: 13, marginTop: 8 }}>
          Add a stylist to the team to set time off and hour exceptions.
        </div>
      </Card>
    );
  }

  return (
    <Card>
      <div className="section-title">Rota &amp; working hours</div>
      <div className="muted" style={{ fontSize: 13, marginTop: 4 }}>
        Time off, closed days and date-specific hours drive availability and the diary.
      </div>

      {lockedStaffId ? (
        <div style={{ marginTop: 12 }}>
          <Pill tone="soft">{selected.displayName} — you manage your own time off</Pill>
        </div>
      ) : (
        <div className="rota-staffbar">
          {chairs.map((s) => (
            <button
              key={s.id}
              className={`rota-staffbar__btn${s.id === selected.id ? ' rota-staffbar__btn--active' : ''}`}
              onClick={() => setSelectedId(s.id)}
            >
              {s.displayName}
            </button>
          ))}
        </div>
      )}

      <StaffRota key={selected.id} staff={selected} tz={tz} />
    </Card>
  );
}

function StaffRota({ staff, tz }: { staff: Staff; tz: string }) {
  return (
    <>
      <WorkingHoursGrid staff={staff} tz={tz} />
      <TimeOffSection staff={staff} tz={tz} />
      <HourExceptionSection staff={staff} />
    </>
  );
}

// ── Time off ───────────────────────────────────────────────────────────────────

function TimeOffSection({ staff, tz }: { staff: Staff; tz: string }) {
  const toast = useToast();
  const { data: entries = [] } = useTimeOff(staff.id);
  const create = useCreateTimeOff(staff.id);
  const remove = useDeleteTimeOff(staff.id);

  const today = todayInZone(tz);
  const [open, setOpen] = useState(false);
  const [startDate, setStartDate] = useState(today);
  const [endDate, setEndDate] = useState(today);
  const [reason, setReason] = useState<TimeOffReason>('holiday');
  const [note, setNote] = useState('');
  const [conflicts, setConflicts] = useState<
    { id: string; clientDisplayName: string; startAt: string; endAt: string }[] | null
  >(null);

  function reset() {
    setStartDate(today);
    setEndDate(today);
    setReason('holiday');
    setNote('');
    setConflicts(null);
  }

  async function add() {
    if (endDate < startDate) {
      toast.error('End date can’t be before the start date.');
      return;
    }
    try {
      const result = await create.mutateAsync({
        startDate,
        endDate,
        reason,
        note: note.trim() || null,
      });
      if (result.conflicts.length > 0) {
        setConflicts(result.conflicts);
        toast.error(
          `${result.conflicts.length} existing booking${result.conflicts.length === 1 ? '' : 's'} fall in this period — rebook or notify.`,
        );
        return; // keep the form open so the conflicts stay visible
      }
      toast.success(`Time off booked for ${staff.displayName}`);
      reset();
      setOpen(false);
    } catch {
      toast.error('Could not book this time off.');
    }
  }

  async function del(t: TimeOff) {
    try {
      await remove.mutateAsync(t.id);
      toast.success('Time off removed');
    } catch {
      toast.error('Could not remove this time off.');
    }
  }

  const sorted = [...entries].sort((a, b) => a.startDate.localeCompare(b.startDate));

  return (
    <div style={{ marginTop: 8 }}>
      <div className="rota-subhead">
        <div className="rota-subhead__title">Time off</div>
        <Button
          size="sm"
          variant={open ? 'outline' : 'soft'}
          onClick={() => {
            reset();
            setOpen((v) => !v);
          }}
        >
          {open ? 'Cancel' : 'Add time off'}
        </Button>
      </div>

      {open ? (
        <Card>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
            <Field label="From">
              <TextInput
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
              />
            </Field>
            <Field label="To">
              <TextInput type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} />
            </Field>
          </div>
          <Field label="Reason">
            <Select value={reason} onChange={(e) => setReason(e.target.value as TimeOffReason)}>
              {REASONS.map(([value, label]) => (
                <option key={value} value={value}>
                  {label}
                </option>
              ))}
            </Select>
          </Field>
          <Field label="Note">
            <TextArea
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="Optional — visible to the team"
            />
          </Field>

          {conflicts && conflicts.length > 0 ? (
            <div className="conflict-list">
              <div className="conflict-list__head">
                {conflicts.length} existing booking{conflicts.length === 1 ? '' : 's'} fall in this
                period — rebook or notify.
              </div>
              {conflicts.map((c) => (
                <div key={c.id} className="conflict-list__row">
                  <span style={{ fontWeight: 600 }}>{c.clientDisplayName || 'Walk-in'}</span>
                  <span className="muted" style={{ fontSize: 12 }}>
                    {spanLabel(c.startAt, c.endAt, tz)}
                  </span>
                </div>
              ))}
              <div className="muted" style={{ fontSize: 12, marginTop: 6 }}>
                The time off is saved — these appointments are not cancelled. Handle them, then
                close.
              </div>
            </div>
          ) : null}

          <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 12 }}>
            <Button onClick={add} disabled={create.isPending}>
              {conflicts ? 'Done' : 'Book time off'}
            </Button>
          </div>
        </Card>
      ) : null}

      {sorted.length === 0 ? (
        <div className="muted" style={{ fontSize: 13, padding: '8px 0' }}>
          No time off booked.
        </div>
      ) : (
        <div className="settings-list">
          {sorted.map((t) => (
            <div key={t.id} className="settings-row">
              <div>
                <div style={{ fontWeight: 600 }}>
                  {t.startDate === t.endDate
                    ? longDate(t.startDate, tz)
                    : `${longDate(t.startDate, tz)} → ${longDate(t.endDate, tz)}`}
                </div>
                <div className="muted" style={{ fontSize: 12.5 }}>
                  {REASON_LABEL[t.reason]}
                  {t.note ? ` · ${t.note}` : ''}
                </div>
              </div>
              <Button size="sm" variant="ghost" onClick={() => del(t)} disabled={remove.isPending}>
                Remove
              </Button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Hour exceptions ──────────────────────────────────────────────────────────────

function HourExceptionSection({ staff }: { staff: Staff }) {
  const toast = useToast();
  const { data: exceptions = [] } = useHourExceptions(staff.id);
  const set = useSetHourException(staff.id);
  const remove = useDeleteHourException(staff.id);

  const [open, setOpen] = useState(false);
  const [date, setDate] = useState('');
  const [kind, setKind] = useState<'closed' | 'custom'>('custom');
  const [startTime, setStartTime] = useState('09:00');
  const [endTime, setEndTime] = useState('17:00');
  const [breakStart, setBreakStart] = useState('');
  const [breakEnd, setBreakEnd] = useState('');

  function reset() {
    setDate('');
    setKind('custom');
    setStartTime('09:00');
    setEndTime('17:00');
    setBreakStart('');
    setBreakEnd('');
  }

  async function save() {
    if (!date) {
      toast.error('Pick a date.');
      return;
    }
    if (kind === 'custom') {
      if (endTime <= startTime) {
        toast.error('End time must be after the start time.');
        return;
      }
      if (breakStart && breakEnd && breakEnd <= breakStart) {
        toast.error('Break end must be after the break start.');
        return;
      }
    }
    try {
      const hasBreak = kind === 'custom' && Boolean(breakStart && breakEnd);
      await set.mutateAsync(
        kind === 'closed'
          ? { date, isClosed: true }
          : {
              date,
              isClosed: false,
              startTime,
              endTime,
              breakStart: hasBreak ? breakStart : null,
              breakEnd: hasBreak ? breakEnd : null,
            },
      );
      toast.success('Hours saved for this date');
      reset();
      setOpen(false);
    } catch {
      toast.error('Could not save these hours.');
    }
  }

  async function del(e: HourException) {
    try {
      await remove.mutateAsync(e.id);
      toast.success('Exception removed');
    } catch {
      toast.error('Could not remove this exception.');
    }
  }

  const sorted = [...exceptions].sort((a, b) => a.date.localeCompare(b.date));

  return (
    <div style={{ marginTop: 8 }}>
      <div className="rota-subhead">
        <div className="rota-subhead__title">Hour exceptions</div>
        <Button
          size="sm"
          variant={open ? 'outline' : 'soft'}
          onClick={() => {
            reset();
            setOpen((v) => !v);
          }}
        >
          {open ? 'Cancel' : 'Add exception'}
        </Button>
      </div>

      {open ? (
        <Card>
          <Field label="Date">
            <TextInput type="date" value={date} onChange={(e) => setDate(e.target.value)} />
          </Field>
          <Field label="This date">
            <Select value={kind} onChange={(e) => setKind(e.target.value as 'closed' | 'custom')}>
              <option value="custom">Custom hours</option>
              <option value="closed">Off (closed this date)</option>
            </Select>
          </Field>
          {kind === 'custom' ? (
            <>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
                <Field label="Start">
                  <TextInput
                    type="time"
                    value={startTime}
                    onChange={(e) => setStartTime(e.target.value)}
                  />
                </Field>
                <Field label="End">
                  <TextInput
                    type="time"
                    value={endTime}
                    onChange={(e) => setEndTime(e.target.value)}
                  />
                </Field>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
                <Field label="Break from" hint="Optional mid-day break">
                  <TextInput
                    type="time"
                    value={breakStart}
                    onChange={(e) => setBreakStart(e.target.value)}
                  />
                </Field>
                <Field label="Break to">
                  <TextInput
                    type="time"
                    value={breakEnd}
                    onChange={(e) => setBreakEnd(e.target.value)}
                  />
                </Field>
              </div>
            </>
          ) : null}
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 12 }}>
            <Button onClick={save} disabled={set.isPending}>
              Save
            </Button>
          </div>
        </Card>
      ) : null}

      {sorted.length === 0 ? (
        <div className="muted" style={{ fontSize: 13, padding: '8px 0' }}>
          No date-specific hours set.
        </div>
      ) : (
        <div className="settings-list">
          {sorted.map((e) => (
            <div key={e.id} className="settings-row">
              <div>
                <div style={{ fontWeight: 600 }}>{e.date}</div>
                <div className="muted" style={{ fontSize: 12.5 }}>
                  {e.isClosed
                    ? 'Off — closed this date'
                    : `${e.startTime ?? '—'} – ${e.endTime ?? '—'}${
                        e.breakStart && e.breakEnd ? ` · break ${e.breakStart}–${e.breakEnd}` : ''
                      }`}
                </div>
              </div>
              <Button size="sm" variant="ghost" onClick={() => del(e)} disabled={remove.isPending}>
                Remove
              </Button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
